import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { formatDeliveryDate } from '../../services/dateUtils';
import { ErrorMessage, SuccessMessage } from '../Messages/StatusMessages';
import { batchSave, logFrontendEvent, storePendingChanges, createCheckoutSession } from '../../services/mealPlanService';
import { MEAL_TYPES, MEAL_PRICES } from '../../constants/meals';
import { groupDatesByWeek, formatWeekHeader } from '../../utils/weekUtils';

/**
 * Shared content component for Order Summary
 * Used by both SummaryModal (mobile) and OrderSummarySidebar (desktop)
 *
 * @param {string} variant - 'modal' or 'sidebar' to adjust styling
 * @param {object} pendingChanges - Pending changes tracked per delivery date
 * @param {function} onSaveSuccess - Callback when save is successful
 */
const OrderSummaryContent = ({
    ordersByDeliveryDate,
    deliveryDates,
    billingChanges,
    customerToken,
    pendingChanges = {},
    priorPaymentsByDate = {},
    paymentRecords = {},
    onSaveSuccess,
    variant = 'modal', // 'modal' or 'sidebar'
    customerDetails
}) => {
    const [paymentLoading, setPaymentLoading] = useState({});
    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [confirmationModal, setConfirmationModal] = useState({ show: false, dates: [], isRefund: false, hasPendingChanges: false });
    // eslint-disable-next-line no-unused-vars
    const [successMessage, setSuccessMessage] = useState(''); // Used for save-only success feedback

    // Define meal type order
    const mealTypeOrder = MEAL_TYPES.reduce((acc, mealType, index) => {
        acc[mealType] = index + 1;
        return acc;
    }, {});

    // Helper function for proper pluralization
    const pluralizeMealType = (mealType, count) => {
        if (count === 1) return mealType;
        if (mealType === 'Lunch') return 'Lunches';
        return `${mealType}s`;
    };

    // Check if any dirty date requires payment (positive amount due after prior payments)
    const taxRate = 0.08875;
    const hasPaymentRequired = () => {
        return displayDates.some(date => {
            if (!pendingChanges[date]?.isDirty) return false;
            const net = billingChanges?.dates?.[date]?.net || 0;
            const fee = billingChanges?.dates?.[date]?.deliveryFee || 0;
            const prior = priorPaymentsByDate[date] || 0;
            const amountDue = net * (1 + taxRate) + fee - prior;
            return amountDue > 0;
        });
    };

    const hasRefundRequired = () => {
        return displayDates.some(date => {
            if (!pendingChanges[date]?.isDirty) return false;
            const net = billingChanges?.dates?.[date]?.net || 0;
            const fee = billingChanges?.dates?.[date]?.deliveryFee || 0;
            const prior = priorPaymentsByDate[date] || 0;
            const amountDue = net * (1 + taxRate) + fee - prior;
            return amountDue < 0;
        });
    };

    // Styling adjustments based on variant
    const isSidebar = variant === 'sidebar';
    const contentPadding = isSidebar ? '20px' : '24px';
    //console.log('dateChanges.changes', pendingChanges);

    // Show confirmation modal when payment/save button is clicked
    // Accepts either a single date string or an array of dates
    const handleButtonClick = (dateOrDates) => {
        const dates = Array.isArray(dateOrDates) ? dateOrDates : [dateOrDates];
        const hasPendingChanges = dates.some(date => pendingChanges[date]?.isDirty);
        const hasRefund = dates.some(date => (billingChanges?.dates?.[date]?.net || 0) < 0);

        logFrontendEvent('SAVE_BUTTON_CLICKED', customerToken, {
            dates,
            hasPendingChanges,
            hasRefund,
            pendingChangesSnapshot: dates.map(d => ({
                date: d,
                isDirty: pendingChanges[d]?.isDirty,
                changesCount: pendingChanges[d]?.changes?.length || 0
            }))
        });

        setConfirmationModal({ show: true, dates, isRefund: hasRefund, hasPendingChanges });
    };

    // Handle confirmed save — routes to Stripe Checkout if charges > 0, or saves directly
    const handleConfirmedPayment = async () => {
        const dates = confirmationModal.dates;
        setCheckoutLoading(true);

        logFrontendEvent('SAVE_CONFIRMED', customerToken, {
            dates,
            changesPerDate: dates.map(d => ({
                date: d,
                isDirty: pendingChanges[d]?.isDirty,
                changesCount: pendingChanges[d]?.changes?.length || 0,
                changeTypes: pendingChanges[d]?.changes?.map(c => c.type) || []
            }))
        });

        // Set loading for all dates
        const loadingState = {};
        dates.forEach(date => { loadingState[date] = true; });
        setPaymentLoading(prev => ({ ...prev, ...loadingState }));

        try {
            // Calculate total charge across all dates (positive nets only)
            const taxRate = 0.08875;
            const lineItems = [];
            const changesPerDate = {};

            for (const date of dates) {
                if (!pendingChanges[date]?.isDirty || !pendingChanges[date]?.changes?.length) continue;

                changesPerDate[date] = { changes: pendingChanges[date].changes };

                const dateData = billingChanges?.dates?.[date];
                const netAmount = dateData?.net || 0;
                const deliveryFee = dateData?.deliveryFee || 0;
                const priorPaid = priorPaymentsByDate[date] || 0;

                // Calculate what's actually owed after subtracting prior payments
                const totalWithTax = netAmount * (1 + taxRate) + deliveryFee;
                const amountDue = totalWithTax - priorPaid;

                if (amountDue > 0) {
                    // Build detailed items list from billing data for Stripe display & logging
                    const items = [];
                    const meals = dateData?.meals || {};
                    Object.entries(meals).forEach(([mealType, mealData]) => {
                        const orderDetails = mealData?.orderDetails || [];
                        orderDetails.forEach(order => {
                            if (!order.isIncluded || (order.premiumProtein?.charge > 0)) {
                                items.push({
                                    name: order.itemName,
                                    quantity: order.quantity,
                                    mealType,
                                    price: order.isIncluded ? 0 : order.price * order.quantity,
                                    premiumProtein: order.premiumProtein?.charge > 0
                                        ? { name: order.premiumProtein.name, charge: order.premiumProtein.charge }
                                        : null
                                });
                            }
                        });
                    });

                    // Human-readable description shown on Stripe Checkout page
                    const descriptionParts = items.map(item => {
                        let desc = `${item.quantity}x ${item.name}`;
                        if (item.premiumProtein) desc += ` (Premium: ${item.premiumProtein.name})`;
                        return desc;
                    });

                    lineItems.push({
                        deliveryDate: date,
                        amount: parseFloat(amountDue.toFixed(2)),
                        description: descriptionParts.join(', ') || 'Meal plan adjustments',
                        items,
                        deliveryFee,
                        priorPaid
                    });
                }
            }

            const totalCharge = lineItems.reduce((sum, item) => sum + item.amount, 0);

            if (totalCharge > 0) {
                // --- STRIPE CHECKOUT FLOW ---
                logFrontendEvent('STRIPE_CHECKOUT_STARTED', customerToken, {
                    dates,
                    totalCharge,
                    lineItems: lineItems
                });

                // 1. Store pending changes server-side
                const { pendingId } = await storePendingChanges(customerToken, changesPerDate, billingChanges);

                // 2. Create Stripe Checkout session
                const { url } = await createCheckoutSession(pendingId, lineItems, customerToken, customerDetails?.customer?.stripeCustomerId);

                // 3. Redirect to Stripe
                window.location.href = url;
                return; // Page will redirect — don't clear loading
            }

            // --- DIRECT SAVE FLOW (no charge or reductions only) ---
            setConfirmationModal({ show: false, dates: [], isRefund: false, hasPendingChanges: false });
            setCheckoutLoading(false);
            const savedDates = [];
            const partialDates = [];
            const failedDates = [];

            for (const date of dates) {
                const hasDateChanges = pendingChanges[date]?.isDirty && pendingChanges[date]?.changes?.length > 0;
                if (!hasDateChanges) continue;

                try {
                    const response = await batchSave(customerToken, date, pendingChanges[date].changes, billingChanges);
                    const results = response.data.results || [];
                    const successfulResults = results.filter(r => r.success);

                    if (response.data.success) {
                        savedDates.push(date);
                        if (onSaveSuccess) onSaveSuccess(date, results, true);
                    } else if (successfulResults.length > 0) {
                        partialDates.push(date);
                        if (onSaveSuccess) onSaveSuccess(date, results, false);
                    } else {
                        failedDates.push(date);
                    }
                } catch (error) {
                    logFrontendEvent('SAVE_DATE_ERROR', customerToken, { date, error: error.message });
                    failedDates.push(date);
                }
            }

            if (savedDates.length > 0 && partialDates.length === 0 && failedDates.length === 0) {
                setSuccessMessage('All changes saved successfully.');
                setTimeout(() => setSuccessMessage(''), 5000);
            } else if (partialDates.length > 0 || (savedDates.length > 0 && failedDates.length > 0)) {
                setErrorMessage('Changes partially saved. Please try again or refresh the page. If the issue persists, contact us.');
            } else if (failedDates.length > 0) {
                setErrorMessage('An error occurred while saving. Please try again or refresh the page. If the issue persists, contact us.');
            }
        } catch (error) {
            logFrontendEvent('SAVE_UNEXPECTED_ERROR', customerToken, {
                error: error.message,
                stack: error.stack
            });
            console.error('Save error:', error);
            setErrorMessage('An error occurred while saving. Please try again or refresh the page. If the issue persists, contact us.');
        } finally {
            setCheckoutLoading(false);
            setConfirmationModal({ show: false, dates: [], isRefund: false, hasPendingChanges: false });
            const clearedLoading = {};
            dates.forEach(date => { clearedLoading[date] = false; });
            setPaymentLoading(prev => ({ ...prev, ...clearedLoading }));
        }
    };

    // Get dates that should be displayed
    const displayDates = (deliveryDates || []).filter(date => {
        const hasOrders = ordersByDeliveryDate?.[date]?.some(order => order.quantity > 0);
        const hasBillingChanges = billingChanges?.dates?.[date]?.hasChanges || billingChanges?.dates?.[date]?.isSkipped;
        const hasPendingChanges = pendingChanges[date]?.isDirty || false;
        return hasOrders || hasBillingChanges || hasPendingChanges;
    });

    // Group orders by meal type for a given date
    const getOrdersByMealType = (date) => {
        const ordersForDate = ordersByDeliveryDate[date] || [];
        const selectedOrders = ordersForDate.filter(order => order.quantity > 0);

        // Group by meal type
        const grouped = {};
        selectedOrders.forEach(order => {
            const mealType = order.meal || 'Other';
            if (!grouped[mealType]) {
                grouped[mealType] = [];
            }
            // Check if order already exists, if so add to quantity
            // Only consolidate if both have no premium protein (price is 0 or undefined/no protein options)
            const existingProteinPrice = order.proteinOptions?.find(opt => opt.isActive)?.price || 0;
            const existing = grouped[mealType].find(o => {
                const oProteinPrice = o.proteinOptions?.find(opt => opt.isActive)?.price || 0;
                return o.itemName === order.itemName && oProteinPrice === 0 && existingProteinPrice === 0;
            });
            if (existing) {
                existing.quantity += order.quantity;
            } else {
                grouped[mealType].push({ ...order });
            }
        });

        return { grouped };
    };

    return (
        <div style={{
            padding: contentPadding,
            backgroundColor: '#f5f0eb',
            minHeight: isSidebar ? '100vh' : 'auto'
        }}>
            {/* Header */}
            <div>
                <h2 style={{
                    color: '#333',
                    fontSize: '18px',
                    fontWeight: '600',
                    marginTop: 0,
                    marginBottom: 0
                }}>
                    Order Summary
                </h2>
                {/* <p style={{
                    color: 'rgb(115, 108, 106)',
                    textAlign: 'left',
                    fontSize: '14px',
                    marginTop: 5,
                    marginBottom: '20px',
                    backgroundColor: 'rgba(248, 231, 40, 0.1)',
                    padding: '10px',
                    borderRadius: '10px',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                }}>
                    ⚠️ Meal edits (except for skipping a delivery) are NOT saved until you complete the payment process or save changes.
                </p> */}
                <p style={{
                    color: 'rgb(115, 108, 106)',
                    textAlign: 'left',
                    fontSize: '14px',
                    marginTop: 5,
                    marginBottom: '20px',
                    backgroundColor: 'rgba(248, 231, 40, 0.1)',
                    padding: '10px',
                    borderRadius: '10px',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                }}>
                    ⚠️ Any unsaved changes will be lost if you close/refresh this page.
                </p>
                <p style={{
                    color: 'rgb(115, 108, 106)',
                    textAlign: 'left',
                    fontSize: '14px',
                    marginTop: 5,
                    marginBottom: '20px',
                    backgroundColor: 'rgba(248, 231, 40, 0.1)',
                    padding: '10px',
                    borderRadius: '10px',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                }}>
                    🧾 We will process any refunds within 5 business days after the delivery date.
                </p>

            </div>

            {/* Status Messages */}
            <ErrorMessage
                message={errorMessage}
                onClose={() => setErrorMessage('')}
            />
            <SuccessMessage
                message={successMessage}
                onClose={() => setSuccessMessage('')}
            />
            {(() => {
                const hasPendingChanges = displayDates.some(date => pendingChanges[date]?.isDirty) || false;
                const isDisabled = !hasPendingChanges || displayDates.some(date => paymentLoading[date]);

                return (
                    <button
                        onClick={() => handleButtonClick(displayDates.filter(date => pendingChanges[date]?.isDirty))}
                        disabled={isDisabled}
                        style={{
                            width: '90%',
                            backgroundColor: isDisabled ? '#ccc' : '#ff7c44',
                            color: 'white',
                            border: 'none',
                            borderRadius: '20px',
                            padding: '18px 16px',
                            fontSize: '16px',
                            fontWeight: '600',
                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                            transition: 'all 0.2s ease',
                            margin: '12px auto 36px',
                            display: 'block',
                        }}
                        onMouseOver={(e) => {
                            if (!isDisabled) {
                                e.target.style.backgroundColor = '#e66a2e';
                            }
                        }}
                        onMouseOut={(e) => {
                            if (!isDisabled) {
                                e.target.style.backgroundColor = '#ff7c44';
                            }
                        }}
                    >
                        {displayDates.some(date => paymentLoading[date])
                            ? 'Processing...'
                            : isDisabled ? 'No Changes to Save' : hasPaymentRequired() ? 'Checkout to Save Changes' : hasRefundRequired() ? 'Save Changes & Schedule Refunds' : 'Save Changes'
                        }
                    </button>
                );
            })()}

            {displayDates.length === 0 ? (
                <div style={{
                    textAlign: 'center',
                    color: '#666',
                    fontSize: '14px',
                    padding: '40px 20px'
                }}>
                    No meals selected for any delivery date.
                </div>
            ) : (
                <div>

                    {displayDates.map((date) => {
                        const billingChangesDateData = billingChanges?.dates?.[date];
                        const { grouped } = getOrdersByMealType(date);

                        // Calculate net for this date
                        const netAmount = billingChangesDateData?.net || 0;

                        return (
                            <div key={date} style={{ marginBottom: '24px' }}>
                                {/* Date Header */}
                                <div style={{
                                    fontWeight: '700',
                                    fontSize: '16px',
                                    color: '#333',
                                    marginBottom: '12px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                }}>
                                    {formatDeliveryDate(date)} Delivery
                                    {billingChangesDateData?.isSkipped && (
                                        <span style={{
                                            color: '#dc3545',
                                            fontWeight: '600',
                                            marginLeft: '8px',
                                            textTransform: 'none'
                                        }}>
                                            (Skipped)
                                        </span>
                                    )}
                                </div>

                                {/* Skipped Date - Simple meal type counts */}
                                {billingChangesDateData?.isSkipped ? (
                                    <div style={{ marginBottom: '12px' }}>
                                        {billingChangesDateData?.meals && Object.entries(billingChangesDateData.meals)
                                            .filter(([_, data]) => data.subscribed > 0)
                                            .sort(([a], [b]) => (mealTypeOrder[a] || 999) - (mealTypeOrder[b] || 999))
                                            .map(([mealType, data], idx, arr) => {
                                                const refundAmount = data.amount || 0;
                                                return (
                                                    <div
                                                        key={mealType}
                                                        style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            padding: '6px 0',
                                                            fontSize: '14px',
                                                            color: '#333',
                                                            borderBottom: idx < arr.length - 1 ? '1px dashed #ddd' : 'none'
                                                        }}
                                                    >
                                                        <span>0 / {data.subscribed} {pluralizeMealType(mealType, data.subscribed)}</span>
                                                        <span style={{
                                                            color: '#333',
                                                            fontStyle: 'normal',
                                                            fontSize: '13px',
                                                            fontWeight: '600',
                                                            marginLeft: '8px',
                                                            whiteSpace: 'nowrap'
                                                        }}>
                                                            -${Math.abs(refundAmount).toFixed(2)}
                                                        </span>
                                                    </div>
                                                );
                                            })
                                        }
                                    </div>
                                ) : (
                                    /* Active Date - Detailed dish listing */
                                    <div style={{ marginBottom: '12px' }}>
                                        {/* Show message if no meals selected */}
                                        {Object.keys(grouped).length === 0 && (
                                            <div style={{
                                                color: '#666',
                                                fontSize: '13px',
                                                fontStyle: 'italic',
                                                padding: '8px 0'
                                            }}>
                                                {pendingChanges[date]?.isDirty
                                                    ? 'All meals for this date are removed (unsaved change)'
                                                    : 'No meals selected for this date.'}
                                            </div>
                                        )}
                                        {/* Get meal types from both orders and billing changes */}
                                        {(() => {
                                            return MEAL_TYPES.map((mealType, mealIdx) => {
                                                const mealData = billingChangesDateData?.meals?.[mealType];
                                                const orderDetails = mealData?.orderDetails || [];
                                                const ordered = mealData?.ordered || 0;
                                                const subscribed = mealData?.subscribed || 0;
                                                const delta = mealData?.delta || 0;
                                                const amount = mealData?.amount || 0;

                                                return (subscribed > 0 || ordered > 0) && (
                                                    <div key={mealType} style={{ marginBottom: mealIdx < MEAL_TYPES.length - 1 ? '12px' : '0' }}>
                                                        {/* Meal Type Header - just shows count */}
                                                        <div style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            padding: '6px 0',
                                                            fontSize: '14px',
                                                            fontWeight: '600',
                                                            color: '#333',
                                                            borderBottom: '1px dashed #ddd'
                                                        }}>
                                                            <span>
                                                                {subscribed > 0
                                                                    ? mealType === 'Snack'
                                                                        ? `${ordered} ${pluralizeMealType(mealType, ordered)} for ${subscribed} ${subscribed === 1 ? 'day' : 'days'} subscribed`
                                                                        : `${ordered} ${pluralizeMealType(mealType, subscribed)}`
                                                                    : `${ordered} ${pluralizeMealType(mealType, ordered)}`}
                                                                {mealType === 'Snack' && (
                                                                    <span style={{
                                                                        fontSize: '12px',
                                                                        color: '#666',
                                                                        fontWeight: '400'
                                                                    }}>
                                                                        <br />(Recommended: {customerDetails.customer.snacksPerDay} snacks per day)
                                                                    </span>
                                                                )}
                                                            </span>

                                                            {amount < 0 && <span style={{
                                                                color: '#333',
                                                                fontStyle: 'normal',
                                                                fontSize: '13px',
                                                                fontWeight: '600',
                                                                marginLeft: '8px',
                                                                whiteSpace: 'nowrap'
                                                            }}>
                                                                -${Math.abs(amount).toFixed(2)}
                                                            </span>}

                                                        </div>

                                                        {/* Individual Dishes with per-meal pricing */}
                                                        {orderDetails.map((orderDetail, orderIdx) => (
                                                            <div
                                                                key={`${orderDetail.recordId}-${orderIdx}`}
                                                                style={{
                                                                    padding: '8px 0 8px 0',
                                                                    fontSize: '13px',
                                                                    color: '#333',
                                                                    borderBottom: orderIdx < orderDetails.length - 1 ? '1px solid #eee' : 'none'
                                                                }}
                                                            >
                                                                {/* Meal name row */}
                                                                <div style={{
                                                                    display: 'flex',
                                                                    justifyContent: 'space-between',
                                                                    alignItems: 'flex-start'
                                                                }}>
                                                                    <span style={{ flex: 1 }}>
                                                                        {orderDetail.itemName} {orderDetail.quantity > 1 ? `(×${orderDetail.quantity})` : ''}
                                                                    </span>
                                                                    <span style={{
                                                                        color: orderDetail.isIncluded ? '#666' : '#333',
                                                                        fontStyle: orderDetail.isIncluded ? 'italic' : 'normal',
                                                                        fontSize: orderDetail.isIncluded ? '12px' : '13px',
                                                                        fontWeight: orderDetail.isIncluded ? '400' : '600',
                                                                        marginLeft: '8px',
                                                                        whiteSpace: 'nowrap'
                                                                    }}>
                                                                        {orderDetail.isIncluded
                                                                            ? 'Included in plan'
                                                                            : `$${(orderDetail.price * orderDetail.quantity).toFixed(2)}`
                                                                        }
                                                                    </span>
                                                                </div>

                                                                {/* Premium protein row */}
                                                                {orderDetail.premiumProtein && (
                                                                    <div style={{
                                                                        display: 'flex',
                                                                        justifyContent: 'space-between',
                                                                        paddingLeft: '12px',
                                                                        marginTop: '4px',
                                                                        fontSize: '12px',
                                                                        color: '#856404',
                                                                        fontStyle: 'italic'
                                                                    }}>
                                                                        <span>Premium Protein: {orderDetail.premiumProtein.name}</span>
                                                                        {orderDetail.premiumProtein.charge > 0 && (
                                                                            <span>+${orderDetail.premiumProtein.charge.toFixed(2)}</span>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                );
                                            });
                                        })()}
                                    </div>
                                )}

                                {/* Subtotal, Tax, Delivery Fee, Prior Payments, and Amount Due */}
                                {(() => {
                                    const deliveryFee = billingChangesDateData?.deliveryFee || 0;
                                    const priorPaid = priorPaymentsByDate[date] || 0;
                                    const hasChanges = netAmount !== 0 || deliveryFee !== 0 || priorPaid > 0;
                                    if (!hasChanges) return null;

                                    const taxRate = 0.08875;
                                    const taxAmount = netAmount * taxRate;
                                    const totalWithTax = netAmount * (1 + taxRate) + deliveryFee;
                                    const amountDue = totalWithTax - priorPaid;

                                    return (
                                        <>
                                            {/* Subtotal */}
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                padding: '8px 0',
                                                borderTop: '1px solid #ccc',
                                                fontSize: '13px'
                                            }}>
                                                <span>Subtotal</span>
                                                <span style={{ color: '#333' }}>
                                                    {netAmount > 0 ? `$${netAmount.toFixed(2)}` : netAmount < 0 ? `-$${Math.abs(netAmount).toFixed(2)}` : '$0.00'}
                                                </span>
                                            </div>

                                            {/* Sales Tax */}
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                padding: '4px 0',
                                                fontSize: '13px'
                                            }}>
                                                <span>Sales Tax (8.875%)</span>
                                                <span style={{ color: '#333' }}>
                                                    {taxAmount >= 0 ? `$${taxAmount.toFixed(2)}` : `-$${Math.abs(taxAmount).toFixed(2)}`}
                                                </span>
                                            </div>

                                            {/* Delivery Fee */}
                                            {deliveryFee !== 0 && (
                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    padding: '4px 0',
                                                    fontSize: '13px'
                                                }}>
                                                    <span>Delivery Fee {(() => {
                                                        const zip = ordersByDeliveryDate?.[date]?.[0]?.zipCode;
                                                        return zip ? <span style={{ color: '#666', fontStyle: 'italic', fontSize: '12px', fontWeight: '400' }}>(Zip Code: {zip})</span> : null;
                                                    })()}</span>
                                                    <span style={{ color: '#333' }}>
                                                        {deliveryFee > 0 ? `$${deliveryFee.toFixed(2)}` : `-$${Math.abs(deliveryFee).toFixed(2)}`}
                                                    </span>
                                                </div>
                                            )}

                                            {/* Total (before prior payments) */}
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                padding: '8px 0',
                                                borderTop: '1px solid #ccc',
                                                fontWeight: '600',
                                                fontSize: '14px'
                                            }}>
                                                <span>Total ({formatDeliveryDate(date)})</span>
                                                <span style={{ color: '#333' }}>
                                                    {totalWithTax > 0 ? `$${totalWithTax.toFixed(2)}` : totalWithTax < 0 ? `-$${Math.abs(totalWithTax).toFixed(2)}` : '$0.00'}
                                                </span>
                                            </div>

                                            {/* Prior payments made */}
                                            {priorPaid > 0 && (
                                                <>
                                                    <div style={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        padding: '4px 0',
                                                        fontSize: '13px',
                                                    }}>
                                                        <span>Payments Made</span>
                                                        <span>-${priorPaid.toFixed(2)}</span>
                                                    </div>

                                                    <div style={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        padding: '6px 0',
                                                        borderTop: '1px solid #ccc',
                                                        fontWeight: '700',
                                                        fontSize: '14px',
                                                    }}>
                                                        <span>{amountDue > 0 ? 'Amount due' : amountDue < 0 ? 'Refund due' : 'Settled'}</span>
                                                        <span>
                                                            {amountDue > 0 ? `$${amountDue.toFixed(2)}` : amountDue < 0 ? `-$${Math.abs(amountDue).toFixed(2)}` : '$0.00'}
                                                        </span>
                                                    </div>
                                                </>
                                            )}
                                        </>
                                    );
                                })()}

                                {/* Payment History for this date */}
                                {paymentRecords[date]?.length > 0 && (
                                    <div style={{
                                        backgroundColor: '#f8f9fa',
                                        borderRadius: '8px',
                                        padding: '10px 12px',
                                        marginTop: '10px',
                                        fontSize: '13px'
                                    }}>
                                        <div style={{ fontWeight: '600', color: '#555', marginBottom: '6px' }}>
                                            Payments History:
                                        </div>
                                        {paymentRecords[date].map((record, idx) => (
                                            <div key={idx} style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                padding: '3px 0',
                                                color: '#666'
                                            }}>
                                                <span>${record.amount.toFixed(2)}</span>
                                                {record.receiptUrl && (
                                                    <a
                                                        href={record.receiptUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{ color: '#ff7c44', fontSize: '12px', textDecoration: 'none' }}
                                                    >
                                                        View receipt
                                                    </a>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Save/Payment Button - show if has pending changes OR monetary changes
                                {(() => {
                                    const hasPendingChanges = pendingChanges[date]?.isDirty || false;
                                    const buttonText = `Save Changes for ${formatDeliveryDate(date)}`;
                                    const isDisabled = !hasPendingChanges || paymentLoading[date];
                                    if (!buttonText) return null;

                                    return (
                                        <button
                                            onClick={() => handleButtonClick(date)}
                                            disabled={isDisabled}
                                            style={{
                                                width: '80%',
                                                backgroundColor: isDisabled ? '#ccc' : 'rgb(255, 177, 60)',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '20px',
                                                padding: '10px 16px',
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                cursor: isDisabled ? 'not-allowed' : 'pointer',
                                                boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                                                transition: 'all 0.2s ease',
                                                margin: '12px auto 0',
                                                display: 'block',

                                            }}
                                            onMouseOver={(e) => {
                                                if (!isDisabled) {
                                                    e.target.style.backgroundColor = '#e66a2e';
                                                }
                                            }}
                                            onMouseOut={(e) => {
                                                if (!isDisabled) {
                                                    e.target.style.backgroundColor = 'rgb(255, 177, 60)';
                                                }
                                            }}
                                        >
                                            {paymentLoading[date]
                                                ? 'Processing...'
                                                : hasPendingChanges
                                                    ? buttonText
                                                    : `No Changes to Save for ${formatDeliveryDate(date)}`
                                            }
                                        </button>
                                    );
                                })()} */}

                                {/* Separator line between dates */}
                                {displayDates.indexOf(date) < displayDates.length - 1 && (
                                    <div style={{
                                        borderBottom: '3px solid #ccc',
                                        marginTop: '24px',
                                        marginBottom: '24px'
                                    }} />
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {(() => {
                const hasPendingChanges = displayDates.some(date => pendingChanges[date]?.isDirty) || false;
                const isDisabled = !hasPendingChanges || displayDates.some(date => paymentLoading[date]);

                return (
                    <button
                        onClick={() => handleButtonClick(displayDates.filter(date => pendingChanges[date]?.isDirty))}
                        disabled={isDisabled}
                        style={{
                            width: '90%',
                            backgroundColor: isDisabled ? '#ccc' : '#ff7c44',
                            color: 'white',
                            border: 'none',
                            borderRadius: '20px',
                            padding: '18px 16px',
                            fontSize: '16px',
                            fontWeight: '600',
                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                            transition: 'all 0.2s ease',
                            margin: '12px auto 36px',
                            display: 'block',
                        }}
                        onMouseOver={(e) => {
                            if (!isDisabled) {
                                e.target.style.backgroundColor = '#e66a2e';
                            }
                        }}
                        onMouseOut={(e) => {
                            if (!isDisabled) {
                                e.target.style.backgroundColor = '#ff7c44';
                            }
                        }}
                    >
                        {displayDates.some(date => paymentLoading[date])
                            ? 'Processing...'
                            : isDisabled ? 'No Changes to Save' : hasPaymentRequired() ? 'Checkout to Save Changes' : hasRefundRequired() ? 'Save Changes & Schedule Refunds' : 'Save Changes'
                        }
                    </button>
                );
            })()}

            {/* Contact Info */}
            <p style={{
                color: '#888',
                textAlign: 'center',
                fontSize: '12px',
                marginTop: '20px',
                marginBottom: 0
            }}>
                Questions? Contact us at <a href="mailto:team@getsquarefare.com" style={{ color: '#ff7c44' }}>team@getsquarefare.com</a>
            </p>

            {/* Confirmation Modal - Using Portal to escape stacking context for Safari compatibility */}
            {confirmationModal.show && createPortal(
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2000
                    }}
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            setConfirmationModal({ show: false, dates: [], isRefund: false, hasPendingChanges: false });
                        }
                    }}
                >
                    <div
                        style={{
                            backgroundColor: '#fff',
                            borderRadius: '20px',
                            padding: '28px 24px',
                            maxWidth: '420px',
                            width: '90%',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.15)'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {(() => {
                            const chargeDates = [];
                            const reductionDates = [];

                            confirmationModal.dates.forEach(date => {
                                const net = billingChanges?.dates?.[date]?.net || 0;
                                const fee = billingChanges?.dates?.[date]?.deliveryFee || 0;
                                const prior = priorPaymentsByDate[date] || 0;
                                const totalWithTax = net * (1 + taxRate) + fee;
                                const amountDue = totalWithTax - prior;

                                if (amountDue > 0.005) {
                                    chargeDates.push({ date, amountDue });
                                } else if (amountDue < -0.005) {
                                    reductionDates.push({ date, amountDue });
                                }
                            });

                            const totalCharge = chargeDates.reduce((sum, d) => sum + d.amountDue, 0);
                            const needsPayment = chargeDates.length > 0;

                            return (
                                <>
                                    {/* Header */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
                                        <div style={{
                                            width: '40px', height: '40px', borderRadius: '50%',
                                            backgroundColor: needsPayment ? 'rgba(255, 124, 68, 0.15)' : 'rgba(40, 167, 69, 0.15)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: '18px', flexShrink: 0
                                        }}>
                                            {needsPayment ? (
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#d4572a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <rect x="1" y="4" width="22" height="16" rx="2" />
                                                    <line x1="1" y1="10" x2="23" y2="10" />
                                                </svg>
                                            ) : '\u2705'}
                                        </div>
                                        <div>
                                            <div style={{ color: '#333', fontSize: '18px', fontWeight: '700' }}>
                                                {needsPayment ? 'Checkout to Save Changes' : hasRefundRequired() ? 'Schedule Refunds & Save Changes' : 'Save changes'}
                                            </div>
                                            {needsPayment && (
                                                <div style={{ color: '#888', fontSize: '13px', marginTop: '2px' }}>
                                                    You will be redirected to Stripe's secure payment page after clicking "Pay".
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Charge breakdown card */}
                                    {needsPayment && (
                                        <div style={{
                                            backgroundColor: '#f8f5f1',
                                            borderRadius: '12px',
                                            padding: '16px',
                                            margin: '16px 0 12px',
                                        }}>
                                            {chargeDates.map((d, i) => (
                                                <div key={d.date} style={{
                                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                                    color: '#555', padding: '6px 0', fontSize: '14px',
                                                    borderBottom: i < chargeDates.length - 1 ? '1px solid #e8e3de' : 'none'
                                                }}>
                                                    <span>{formatDeliveryDate(d.date)} Meal Changes</span>
                                                    <span style={{ fontWeight: '600', color: '#333' }}>${d.amountDue.toFixed(2)}</span>
                                                </div>
                                            ))}
                                            {/* Total */}
                                            <div style={{
                                                display: 'flex', justifyContent: 'flex-end',
                                                borderTop: '1px solid #d5d0cb', marginTop: '8px', paddingTop: '10px',
                                            }}>
                                                <span style={{ fontSize: '22px', fontWeight: '700', color: '#c0562b' }}>
                                                    ${totalCharge.toFixed(2)}
                                                </span>
                                            </div>
                                        </div>
                                    )}

                                    {/* Reduction info */}
                                    {reductionDates.length > 0 && (
                                        <div style={{
                                            backgroundColor: 'rgba(40, 167, 69, 0.08)',
                                            borderRadius: '12px',
                                            padding: '16px',
                                            margin: needsPayment ? '0 0 12px' : '16px 0 12px',
                                        }}>
                                            <div style={{ fontSize: '12px', color: '#999', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                Refunds
                                                <br />[processed manually every Monday night]
                                            </div>
                                            {reductionDates.map(d => (
                                                <div key={d.date} style={{ display: 'flex', justifyContent: 'space-between', color: '#555', padding: '4px 0', fontSize: '14px' }}>
                                                    <span>{formatDeliveryDate(d.date)} delivery</span>
                                                    <span style={{ fontWeight: '600', color: '#28a745' }}>-${Math.abs(d.amountDue).toFixed(2)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* No-cost message */}
                                    {!needsPayment && (
                                        <p style={{ color: '#666', fontSize: '14px', textAlign: 'center', margin: '16px 0', lineHeight: '1.5' }}>
                                            {confirmationModal.dates.length === 1
                                                ? <>Your edits for <b style={{ color: '#333' }}>{formatDeliveryDate(confirmationModal.dates[0])}</b> will be saved.</>
                                                : <>Your edits for <b style={{ color: '#333' }}>{confirmationModal.dates.map(d => formatDeliveryDate(d)).join(', ')}</b> will be saved.</>
                                            }
                                        </p>
                                    )}

                                    {/* Payment warning */}
                                    {needsPayment && (
                                        <div style={{
                                            backgroundColor: 'rgba(255, 124, 68, 0.08)',
                                            borderRadius: '10px',
                                            padding: '12px 14px',
                                            marginBottom: '16px',
                                            display: 'flex', alignItems: 'flex-start', gap: '10px',
                                        }}>
                                            <span style={{ fontSize: '16px', lineHeight: '1.3', flexShrink: 0 }}>&#9888;</span>
                                            <span style={{ color: '#666', fontSize: '13px', lineHeight: '1.5' }}>
                                                Changes are only saved after payment. If you cancel, your unsaved changes will be lost.
                                            </span>
                                        </div>
                                    )}

                                    {/* Buttons */}
                                    <div style={{ display: 'flex', gap: '12px' }}>
                                        <button
                                            onClick={() => setConfirmationModal({ show: false, dates: [], isRefund: false, hasPendingChanges: false })}
                                            disabled={checkoutLoading}
                                            style={{
                                                flex: 1,
                                                padding: '14px 20px',
                                                backgroundColor: '#fff',
                                                color: checkoutLoading ? '#ccc' : '#666',
                                                border: '1px solid #ddd',
                                                borderRadius: '12px',
                                                fontSize: '15px',
                                                fontWeight: '600',
                                                cursor: checkoutLoading ? 'not-allowed' : 'pointer'
                                            }}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={handleConfirmedPayment}
                                            disabled={checkoutLoading}
                                            style={{
                                                flex: 1.2,
                                                padding: '14px 20px',
                                                backgroundColor: checkoutLoading ? '#ccc' : '#d4572a',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '12px',
                                                fontSize: '15px',
                                                fontWeight: '700',
                                                cursor: checkoutLoading ? 'not-allowed' : 'pointer',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                                            }}
                                        >
                                            {checkoutLoading ? 'Processing...' : (
                                                <>
                                                    {needsPayment && (
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <rect x="1" y="4" width="22" height="16" rx="2" />
                                                            <line x1="1" y1="10" x2="23" y2="10" />
                                                        </svg>
                                                    )}
                                                    {needsPayment ? `Pay $${totalCharge.toFixed(2)}` : 'Save'}
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </>
                            );
                        })()}
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default OrderSummaryContent;
